<template>
  <div>
    <div class="tw-flex tw-justify-between">
      <div class="tw-text-xl tw-font-light tw-my-auto">Prescriptions</div>
      <div class="tw-flex">
        <base-button type="text" size="sm" @click="openFilters()">
          <template #icon>
            <filter-icon />
          </template>
          Filters
        </base-button>
        <export-button
          class="tw-ml-5"
          size="sm"
          type="primary"
          label="Export"
          :filename="`PredictilePrescriptions.csv`"
          :requestPayload="requestPayload"
          :url="`/businesses/${businessId}/reports/prescriptions/export`"
          @onError="didErrorExport"
        >
        </export-button>
      </div>
    </div>
    <filter-display class="tw-mt-3" :filters="filters" />
    <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-4 tw-gap-4">
      <aggregate-list
        class="sm:tw-col-span-2 summarylist"
        :class="{ 'tw-overflow-y-scroll': locationAggregates.length > 6 }"
        :aggregates="locationAggregates"
        :isLoading="isLoading"
        labelKey="name"
        idKey="businessLocationId"
        valueKey="count"
        title="Locations"
      />
      <aggregate-list
        class="summarylist"
        :class="{
          'tw-overflow-y-scroll': programGroupAggregates.length > 6,
        }"
        :aggregates="programGroupAggregates"
        :isLoading="isLoading"
        labelKey="group"
        idKey="group"
        valueKey="count"
        title="Program Groups"
      />
      <aggregate-list
        class="summarylist"
        :class="{ 'tw-overflow-y-scroll': statusAggregates.length > 6 }"
        :aggregates="statusAggregates"
        :isLoading="isLoading"
        labelKey="label"
        idKey="status"
        valueKey="count"
        title="Status"
      />
    </div>
    <div class="tw-mt-7 ">{{ page.total }} prescriptions</div>
    <base-table :loading="isLoading" :fields="fields" :items="prescriptions" :sort-key.sync="sortKey" :sort-desc.sync="sortDesc" :loadingRows="5" class="tw-mt-2">
      <template #member="{ item: member }">
        <div class="tw-flex">
          <base-button type="text" size="md" @click="view(member.userId)">
            <span class="tw-whitespace-nowrap tw-text-left">{{ member.fullName }}</span>
          </base-button>
        </div>
      </template>
      <template #status="{ item: member }">
        <div class="tw-text-gray-700 tw-font-normal tw-whitespace-nowrap">{{ capitalize(member.status) }}</div>
      </template>
      <template #prescription="{ item: member }">
        <div class="tw-whitespace-nowrap">{{ member.businessProgramName }}</div>
      </template>
      <template #address="{ item: member }">
        <div class="tw-text-gray-700 tw-text-xs tw-whitespace-nowrap">{{ address(member.addressLine1, member.addressLine2) }}</div>
        <div class="tw-text-gray-700 tw-text-xs tw-whitespace-nowrap">{{ member.city + ", " + member.state + " " + member.postalCode }}</div>
      </template>
      <template #phone="{ item: member }">
        <base-phone-number-field class="" :value="member.phoneNumber" />
      </template>
      <template #email="{ item: member }">
        <div class="tw-whitespace-nowrap">{{ member.email }}</div>
      </template>
      <template #submittedDate="{ item: member }">
        <div class="tw-text-sm tw-text-gray-700 tw-font-normal tw-whitespace-nowrap">{{ formatDate(member.createdDate) }}</div>
      </template>
      <template #lastUpdated="{ item: member }">
        <div class="tw-text-sm tw-text-gray-700 tw-font-normal tw-whitespace-nowrap">{{ formatDate(member.lastUpdatedDate) }}</div>
      </template>
      <template #location="{ item: member }">
        <div class="tw-flex">
          <div>
            <div class="tw-text-sm tw-text-gray-700 tw-font-normal tw-whitespace-nowrap">
              {{ member.businessLocationName }}
            </div>
          </div>
        </div>
      </template>
      <template #medications="{ item: member }">
        <div v-for="med in member.medications" :key="med.businessItemCategoryId">
          <div class="tw-text-xs tw-whitespace-nowrap">{{ med.name }}</div>
        </div>
      </template>
      <template #empty-state>
        <div class="tw-flex tw-justify-center tw-py-5">
          No Results
        </div>
      </template>
      <template #loading-row>
        <td class="tw-py-3 tw-px-4">
          <base-skeleton-loader class="tw-h-3 tw-w-24" />
        </td>
        <td class="tw-py-3 tw-px-4">
          <base-skeleton-loader class="tw-h-3 tw-w-12" />
        </td>
        <td class="tw-py-3 tw-px-4">
          <base-skeleton-loader class="tw-h-3 tw-w-48" />
        </td>
        <td class="tw-py-3 tw-px-4">
          <base-skeleton-loader class="tw-h-3 tw-w-56" />
        </td>
        <td class="tw-py-3 tw-px-4">
          <base-skeleton-loader class="tw-h-3 tw-w-24" />
        </td>
        <td class="tw-py-3 tw-px-4">
          <base-skeleton-loader class="tw-h-3 tw-w-24" />
        </td>
        <td class="tw-py-3 tw-px-4">
          <base-skeleton-loader class="tw-h-3 tw-w-24" />
        </td>
        <td class="tw-py-3 tw-px-4">
          <base-skeleton-loader class="tw-h-3 tw-w-24" />
        </td>
        <td class="tw-py-3 tw-px-4">
          <base-skeleton-loader class="tw-h-2 tw-w-64" />
          <base-skeleton-loader class="tw-h-2 tw-w-64" />
        </td>
      </template>
    </base-table>
    <base-pagination v-model="page" class="tw-mt-2" />
    <report-filter-slideover
      ref="filterSlideOver"
      :business-id="businessId"
      date-filter-label="Submitted"
      :show-plans-filter="false"
      :show-plan-groups-filter="false"
      :show-plan-status-filter="false"
      :showPatientStateFilter="true"
      @filter-updated="applyFilter"
    />
  </div>
</template>

<script>
import ReportFilterSlideover from "@/views/business/reports/components/ReportFilterSlideover";
import FilterDisplay from "@/views/business/reports/components/FilterDisplay";
import AggregateList from "@/views/business/reports/components/AggregateList";
import { FilterIcon } from "@/assets/svgs";
import { mapGetters } from "vuex";
import moment from "moment";
import { capitalize } from "lodash";
import ExportButton from "@/components/ExportButton.vue";

const TAKE_KEY = "predictile_business_prescriptions_report_take";

export default {
  name: "BusinessPrescriptionsReport",
  components: {
    ReportFilterSlideover,
    FilterDisplay,
    AggregateList,
    FilterIcon,
    ExportButton,
  },
  props: {
    businessId: String,
  },
  data: function() {
    return {
      prescriptions: [],
      page: {
        take: localStorage.getItem(TAKE_KEY) ? +localStorage.getItem(TAKE_KEY) : 50,
        skip: 0,
        total: 0,
      },
      isLoading: false,
      filters: {
        startDate: null,
        endDate: null,
        locationsFilter: [],
        programsFilter: [],
        programGroupsFilter: [],
        prescriptionStatusFilter: [],
        statesFilter: [],
      },
      sortKey: "submittedDate",
      sortDesc: true,
      statusAggregates: [],
      programGroupAggregates: [],
      locationAggregates: [],
    };
  },
  computed: {
    ...mapGetters("organization", ["organization", "cobrandId"]),
    fields() {
      let columns = [];

      columns.push({ title: this.organization.customerLabelSingular, key: "member", isSortable: true });
      columns.push({ title: "Status", key: "status" });
      columns.push({ title: "Prescription", key: "prescription", isSortable: true });
      columns.push({ title: "Location", key: "location", isSortable: true });
      columns.push({ title: "Address", key: "address" });
      columns.push({ title: "Phone", key: "phone" });
      columns.push({ title: "Email", key: "email" });
      columns.push({ title: "Submitted", key: "submittedDate", isSortable: true });
      columns.push({ title: "Updated", key: "lastUpdated", isSortable: true });
      columns.push({ title: "Medications", key: "medications" });

      return columns;
    },
    requestPayload() {
      let locationFilterStrings = this.filters.locationsFilter
        ? this.filters.locationsFilter.map(function(item) {
            return item["businessLocationId"];
          })
        : [];
      let programFilterStrings = this.filters.programsFilter
        ? this.filters.programsFilter.map(function(item) {
            return item["businessProgramId"];
          })
        : [];
      let statesFiltervalues = this.filters.statesFilter
        ? this.filters.statesFilter.map(function(item) {
            return item["value"];
          })
        : [];

      return {
        startDate: this.filters.startDate,
        endDate: this.filters.endDate,
        businessLocationIds: locationFilterStrings,
        programGroups: this.filters.programGroupsFilter,
        businessProgramIds: programFilterStrings,
        prescriptionStatuses: this.filters.prescriptionStatusFilter,
        states: statesFiltervalues,
        sortColumn: this.sortKey,
        sortOrder: this.sortDesc === true ? "desc" : "asc",
      };
    },
  },
  watch: {
    page(newValue) {
      localStorage.setItem(TAKE_KEY, newValue.take);
      this.loadPage();
    },
    sortKey() {
      this.loadPage();
    },
    sortDesc() {
      this.loadPage();
    },
  },
  created() {
    this.loadPage();
  },
  methods: {
    didErrorExport(err) {
      this.EventBus.$emit("GLOBAL_ERROR", {
        title: "Failed to download CSV",
        message: "We had a problem downloading the report. Please reload the page to try again.",
        error: err,
      });
    },
    capitalize,
    address(line1, line2) {
      return line2 === null ? line1 : line1 + ", " + line2;
    },
    openFilters() {
      this.$refs.filterSlideOver.show();
    },
    applyFilter(filters) {
      this.filters = filters;
      this.loadPage();
    },
    next() {
      this.page.skip += this.page.take;
    },
    previous() {
      this.page.skip -= this.page.take;
    },
    loadPage() {
      this.isLoading = true;
      let url = `/businesses/${this.businessId}/reports/prescriptions/search?skip=${this.page.skip}&take=${this.page.take}`;

      this.$axios
        .post(url, this.requestPayload)
        .then((res) => {
          this.prescriptions = res.data.page.items;
          this.page.total = res.data.page.total;
          this.statusAggregates = res.data.statusAggregates;
          this.programGroupAggregates = res.data.programGroupAggregates;
          this.locationAggregates = res.data.locationAggregates;
        })
        .catch((err) => {
          this.EventBus.$emit("GLOBAL_ERROR", {
            title: "Failed to load prescriptions",
            message: "We had a problem retrieving the list of prescriptions. Please reload the page to try again.",
            error: err,
          });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    formatDate(date) {
      return new moment.utc(date).local().format("M/D/YYYY");
    },
    view(userId) {
      this.$router.push(`/businesses/${this.businessId}/members/${userId}/prescriptions`);
    },
  },
};
</script>
<style scoped>
.summarylist {
  max-height: 247px;
}
</style>
